import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { usageAPI, stlAPI, imageAPI, supportAPI } from '../lib/api';
import { ROISummary } from '../components/ROIDisplay';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner';
import {
  Layers,
  Wrench,
  Eye,
  TrendingUp,
  Activity,
  Zap,
  Upload,
  Image as ImageIcon,
  FolderOpen,
  TreeDeciduous,
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [roiData, setRoiData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [usageRes, stlRes, imageRes, roiRes, supportRes] = await Promise.allSettled([
        usageAPI.getCurrent(),
        stlAPI.getHistory(5),
        imageAPI.getHistory(5),
        supportAPI.getRoiSummary(),
        supportAPI.getHistory(5),
      ]);

      if (usageRes.status === 'fulfilled') setUsage(usageRes.value.data);
      if (roiRes.status === 'fulfilled') setRoiData(roiRes.value.data);
      
      const stlData = stlRes.status === 'fulfilled' ? stlRes.value.data : [];
      const imageData = imageRes.status === 'fulfilled' ? imageRes.value.data : [];
      const supportData = supportRes.status === 'fulfilled' ? (supportRes.value.data.generations || []) : [];
      
      const combined = [
        ...stlData.map(a => ({ ...a, type: 'STL' })),
        ...imageData.map(a => ({ ...a, type: 'Image' })),
        ...supportData.map(a => ({ ...a, type: 'Support', status: a.status || 'completed', filename: a.original_filename || 'Support Generation' })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
      
      setRecentAnalyses(combined);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const corePillars = [
    {
      title: 'AI Support Engine',
      description: 'Generate intelligent tree supports for your 3D models',
      icon: Layers,
      color: 'primary',
      path: '/dashboard/smart-support',
    },
    {
      title: 'Repair & Analysis',
      description: 'Analyze and repair STL geometry issues',
      icon: Wrench,
      color: 'accent',
      path: '/dashboard/repair-analysis',
    },
    {
      title: 'Vision Diagnosis',
      description: 'Upload print failure photos for AI diagnosis',
      icon: Eye,
      color: 'orange-400',
      path: '/dashboard/diagnosis',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      {/* Welcome Section */}
      <div>
        <h1 className="text-3xl font-bold text-text-primary mb-2">
          Welcome back, {user?.full_name || user?.email?.split('@')[0]}!
        </h1>
        <p className="text-text-muted">Your 3D printing command center</p>
      </div>

      {/* Core Pillars */}
      <div className="grid md:grid-cols-3 gap-6">
        {corePillars.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.title}
              onClick={() => navigate(action.path)}
              className="bg-bg-light p-6 rounded-xl border border-text-muted/20 hover:border-primary/40 transition text-left group card-shadow"
              data-testid={`quick-action-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`w-12 h-12 bg-${action.color}/20 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition`}>
                <Icon className={`w-6 h-6 text-${action.color}`} />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-1">{action.title}</h3>
              <p className="text-sm text-text-secondary">{action.description}</p>
            </button>
          );
        })}
      </div>

      {/* ROI Summary */}
      <ROISummary data={roiData} />

      {/* Usage Stats */}
      <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-text-primary">Monthly Usage</h2>
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-accent" />
            <span className="text-sm text-text-secondary capitalize">
              {usage?.subscription_plan} Plan
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* STL Analyses */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">STL Analyses</span>
              <span className="text-sm font-semibold text-text-primary">
                {usage?.stl_analyses_used || 0} / {usage?.stl_analyses_limit || 0}
              </span>
            </div>
            <div className="w-full bg-bg-dark rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{
                  width: `${getUsagePercentage(usage?.stl_analyses_used, usage?.stl_analyses_limit)}%`,
                }}
              />
            </div>
          </div>

          {/* Image Analyses */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Image Analyses</span>
              <span className="text-sm font-semibold text-text-primary">
                {usage?.image_analyses_used || 0} / {usage?.image_analyses_limit || 0}
              </span>
            </div>
            <div className="w-full bg-bg-dark rounded-full h-2">
              <div
                className="bg-accent h-2 rounded-full transition-all"
                style={{
                  width: `${getUsagePercentage(usage?.image_analyses_used, usage?.image_analyses_limit)}%`,
                }}
              />
            </div>
          </div>

          {/* Chat Messages */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Chat Messages</span>
              <span className="text-sm font-semibold text-text-primary">
                {usage?.chat_messages_used || 0} / {usage?.chat_messages_limit || 0}
              </span>
            </div>
            <div className="w-full bg-bg-dark rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{
                  width: `${getUsagePercentage(usage?.chat_messages_used, usage?.chat_messages_limit)}%`,
                }}
              />
            </div>
          </div>
        </div>

        {usage?.stl_analyses_used >= usage?.stl_analyses_limit && (
          <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <p className="text-sm text-warning">
              You've reached your monthly limit. Upgrade your plan for more analyses!
            </p>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-bg-light p-6 rounded-xl border border-text-muted/20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-text-primary">Recent Activity</h2>
          <TrendingUp className="w-5 h-5 text-accent" />
        </div>

        {recentAnalyses.length === 0 ? (
          <div className="text-center py-12">
            <Zap className="w-12 h-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No analyses yet. Start by uploading an STL file!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentAnalyses.map((analysis, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-bg-dark rounded-lg hover:bg-[#1f2332] transition"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    analysis.type === 'STL' ? 'bg-primary/20' :
                    analysis.type === 'Support' ? 'bg-green-500/20' :
                    'bg-accent/20'
                  }`}>
                    {analysis.type === 'STL' ? (
                      <Upload className="w-5 h-5 text-primary" />
                    ) : analysis.type === 'Support' ? (
                      <TreeDeciduous className="w-5 h-5 text-green-400" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-accent" />
                    )}
                  </div>
                  <div>
                    <p className="text-text-primary font-medium">
                      {analysis.type === 'STL'
                        ? analysis.filename
                        : analysis.type === 'Support'
                        ? analysis.filename
                        : `Image Analysis #${analysis.id?.slice(0, 8) || '?'}`}
                    </p>
                    <p className="text-sm text-text-secondary">
                      {new Date(analysis.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  analysis.status === 'completed'
                    ? 'bg-success/20 text-success'
                    : analysis.status === 'failed'
                    ? 'bg-error/20 text-error'
                    : 'bg-warning/20 text-warning'
                }`}>
                  {analysis.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My Projects Quick Access */}
      <div className="bg-gradient-to-br from-primary/10 to-accent/10 p-6 rounded-xl border border-primary/20">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-1">My Projects</h3>
            <p className="text-sm text-text-secondary">View all your uploads, analyses, and generated supports in one place</p>
          </div>
          <button
            onClick={() => navigate('/dashboard/projects')}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition"
            data-testid="view-projects-btn"
          >
            <FolderOpen className="w-4 h-4" />
            View Projects
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
